import { Vector3, Mesh, Axis } from '@babylonjs/core';

import { Point, Polygon } from '@flatten-js/core';
import { TPoint2D, TPlaneContext, TDimension2D, TColor, TMaterial } from './type';

export const getMeshWidth = (mesh: Mesh, precision: number = 3) => {
	if (!mesh.geometry) return 0;
	return +(mesh.geometry.extend.maximum.x - mesh.geometry.extend.minimum.x).toFixed(precision);
};

export const getMeshLength = (mesh: Mesh, precision: number = 3) => {
	if (!mesh.geometry) return 0;
	return +(mesh.geometry.extend.maximum.z - mesh.geometry.extend.minimum.z).toFixed(precision);
};

export const getMeshHeight = (mesh: Mesh, precision: number = 3) => {
	if (!mesh.geometry) return 0;
	return +(mesh.geometry.extend.maximum.y - mesh.geometry.extend.minimum.y).toFixed(precision);
};

/**
 * Projects a 3D point onto a 2D plane defined by an origin and two basis vectors u and v.
 * This is useful for converting 3D coordinates to 2D coordinates relative to a specific plane.
 *
 * @param {Object} params - Parameters object
 * @param {Vector3} params.point3D - The 3D point to project
 * @param {Vector3} params.origin - The origin point of the plane
 * @param {Vector3} params.u - The first basis vector of the plane
 * @param {Vector3} params.v - The second basis vector of the plane
 * @returns {TPoint2D} A 2D point [u, v] coordinates on the plane
 */
export const projectPoint3DTo2D = (params: {
	point3D: Vector3;
	origin: Vector3;
	u: Vector3;
	v: Vector3;
}): TPoint2D => {
	const { point3D, origin, u, v } = params;
	const d: Vector3 = point3D.subtract(origin);

	return [Vector3.Dot(d, u), Vector3.Dot(d, v)];
};

/**
 * Computes the normal vector of a plane from an array of 3D points.
 * It finds the first valid normal by taking cross products of consecutive triangles and normalizes it.
 * If no valid normal is found, it defaults to the up vector (Y-axis).
 *
 * @param {Array<Vector3>} points3D - Array of 3D points defining the plane
 * @returns {Vector3} The normalized normal vector of the plane
 */
export const computePlaneNormal = (points3D: Vector3[]): Vector3 => {
	if (points3D.length < 3) return Vector3.Zero();

	const a: Vector3 = points3D[0];
	let normal: Vector3 = Vector3.Zero();
	let foundNormal: boolean = false;

	for (let i: number = 1; i < points3D.length - 1; i++) {
		const b: Vector3 = points3D[i];
		const c: Vector3 = points3D[i + 1];

		const v1: Vector3 = b.subtract(a);
		const v2: Vector3 = c.subtract(a);

		normal = Vector3.Cross(v1, v2);

		if (normal.lengthSquared() > 0.000001) {
			normal.normalize();
			foundNormal = true;
			break;
		}
	}

	if (!foundNormal) return Vector3.Up();

	if (Vector3.Dot(normal, Axis.Y) < 0) {
		normal.scaleInPlace(-1);
	}

	return normal;
};

/**
 * Builds an orthonormal basis (u, v) from a given normal vector.
 * It chooses a reference vector (Y or X axis) to cross with the normal to get u, then crosses u with normal to get v.
 * This creates a coordinate system on the plane perpendicular to the normal.
 *
 * @param {Vector3} normal - The normal vector of the plane
 * @returns {Object} Object containing u and v basis vectors
 * @returns {Vector3} returns.u - The first orthonormal basis vector
 * @returns {Vector3} returns.v - The second orthonormal basis vector
 */
export const buildBasisFromNormal = (normal: Vector3): { u: Vector3; v: Vector3 } => {
	const ref: Vector3 = Math.abs(normal.y) < 0.9 ? Axis.Y : Axis.X;
	const u: Vector3 = Vector3.Cross(normal, ref).normalize();
	const v: Vector3 = Vector3.Cross(normal, u).normalize();

	return { u, v };
};

/**
 * Checks if a 2D point is inside a given 2D polygon.
 * Uses the flatten-js library to create a polygon object and test containment.
 * Returns true if the point is inside or on the boundary of the polygon.
 *
 * @param {TPoint2D} point2D - The 2D point to test
 * @param {Array<TPoint2D>} polygon2D - The 2D polygon points
 * @returns {boolean} True if the point is inside or on the boundary of the polygon
 */
export const isPointInsidePolygon = (point2D: TPoint2D, polygon2D: Array<TPoint2D>): boolean => {
	const polygon: Polygon = new Polygon(polygon2D.map((pt: TPoint2D) => new Point(pt[0], pt[1])));
	const point: Point = new Point(point2D[0], point2D[1]);

	return polygon.contains(point);
};

/**
 * Checks if a 2D polygon is completely inside another 2D polygon.
 * Uses the flatten-js library to create polygon objects and test containment.
 * Returns true if the inner polygon is completely contained within the outer polygon.
 *
 * @param {Array<TPoint2D>} innerPolygon2DPoints - The points of the polygon to test
 * @param {Array<TPoint2D>} outerPolygon2DPoints - The points of the outer containing polygon
 * @returns {boolean} True if the inner polygon is completely inside the outer polygon
 */
export const isPolygonInsidePolygon = (
	innerPolygon2DPoints: Array<TPoint2D>,
	outerPolygon2DPoints: Array<TPoint2D>,
): boolean => {
	const innerPolygon: Polygon = new Polygon(
		innerPolygon2DPoints.map(([u, v]) => new Point(u, v)),
	);

	const outerPolygon: Polygon = new Polygon(
		outerPolygon2DPoints.map(([u, v]) => new Point(u, v)),
	);

	return outerPolygon.contains(innerPolygon);
};

/**
 * Checks if a rectangle defined by a 3D position, dimensions, and anchor is completely inside a plane's 2D polygon.
 * Projects the 3D position to 2D, builds the rectangle's corners based on anchor, and checks if the rectangle polygon is contained within the plane's polygon.
 * Useful for validating component placement on walls or floors.
 *
 * @param {Object} params - Parameters object
 * @param {Vector3} params.position3D - The 3D position of the rectangle center
 * @param {TPlaneContext} params.ctx - The plane context with origin, basis vectors, and polygon
 * @param {TDimension2D} params.dimension - The rectangle dimensions (width and height)
 * @param {string} params.anchor - The anchor point type ('bottom', 'top', or 'centre')
 * @returns {boolean} True if the rectangle is completely inside the plane's polygon
 */
export const isInsidePlane = (params: {
	position3D: Vector3;
	ctx: TPlaneContext;
	dimension: TDimension2D;
	anchor: string;
}): boolean => {
	const { position3D, ctx, dimension, anchor } = params;
	const position2D: TPoint2D = projectPoint3DTo2D({
		point3D: position3D,
		origin: ctx.origin,
		u: ctx.u,
		v: ctx.v,
	});
	const corners: Array<TPoint2D> = buildCorners2D({ position2D, dimension, anchor });
	console.log('corners:', corners);
	console.log('ctx.polygon2D:', ctx.polygon2D);
	return isPolygonInsidePolygon(corners, ctx.polygon2D);
};

/**
 * Builds the four 2D corners of a rectangle based on its center position, dimensions, and anchor type.
 * Supports 'bottom', 'top', and 'centre' anchors, positioning the rectangle accordingly.
 * Returns an array of four points in counter-clockwise order starting from top-left relative to the anchor.
 *
 * @param {Object} params - Parameters object
 * @param {TPoint2D} params.position2D - The 2D center position of the rectangle
 * @param {TDimension2D} params.dimension - The rectangle dimensions (width and height)
 * @param {string} params.anchor - The anchor point type ('bottom', 'top', or 'centre')
 * @returns {Array<TPoint2D>} Array of four 2D corner points
 */
export const buildCorners2D = (params: {
	position2D: TPoint2D;
	dimension: TDimension2D;
	anchor: string;
}): Array<TPoint2D> => {
	const { position2D, dimension, anchor } = params;
	const { width, height } = dimension;
	const [cx, cy] = position2D;
	const hw = width / 2;

	if (anchor === 'bottom') {
		return [
			[cx + hw, cy],
			[cx - hw, cy],
			[cx + hw, cy - height],
			[cx - hw, cy - height],
		];
	}

	if (anchor === 'top') {
		return [
			[cx + hw, cy],
			[cx - hw, cy],
			[cx + hw, cy + height],
			[cx - hw, cy + height],
		];
	}

	// centre anchor (default)
	const hh = height / 2;

	return [
		[cx + hw, cy + hh],
		[cx + hw, cy - hh],
		[cx - hw, cy + hh],
		[cx - hw, cy - hh],
	];
};

/**
 * Clamps a desired 3D position to move only within the bounds of a frame delta along the u and v axes.
 * Projects the movement onto the plane's axes and limits each component to the frame delta magnitude.
 * Returns the clamped position or null if there's no movement, preventing overshooting during drag operations.
 *
 * @param {Object} params - Parameters object
 * @param {Vector3} params.desiredPosition - The target 3D position
 * @param {Vector3} params.lastValid - The last known valid position
 * @param {Vector3} params.delta - The frame movement delta vector
 * @param {Vector3} params.u - The u basis vector of the plane
 * @param {Vector3} params.v - The v basis vector of the plane
 * @returns {Vector3|null} The clamped position, or null if no valid movement
 */
export const clampToFrameDelta = (params: {
	desiredPosition: Vector3;
	lastValid: Vector3;
	delta: Vector3;
	u: Vector3;
	v: Vector3;
}): Vector3 | null => {
	const { desiredPosition, lastValid, delta, u, v } = params;
	// How far the cursor wants to move from the last valid position.
	const pull: Vector3 = desiredPosition.subtract(lastValid);

	// Project pull and the frame delta onto each wall axis.
	const pU: number = Vector3.Dot(pull, u);
	const pV: number = Vector3.Dot(pull, v);

	// Cap each axis to at most one frame-delta of movement.
	const stepU: number =
		Math.abs(pU) > Math.abs(Vector3.Dot(delta, u))
			? Math.sign(pU) * Math.abs(Vector3.Dot(delta, u))
			: pU;
	const stepV: number =
		Math.abs(pV) > Math.abs(Vector3.Dot(delta, v))
			? Math.sign(pV) * Math.abs(Vector3.Dot(delta, v))
			: pV;

	// No movement on either axis — nothing to clamp.
	if (stepU === 0 && stepV === 0) return null;

	// Reconstruct the clamped 3D position from the two axis steps.
	return lastValid.add(u.scale(stepU)).add(v.scale(stepV));
};

/**
 * Finds the closest point on a 2D polygon's boundary to a given 2D point.
 * Uses the flatten-js library to calculate the shortest distance and returns the point on the polygon edge.
 * Returns null if the polygon has fewer than 2 points, as it can't form a valid boundary.
 *
 * @param {TPoint2D} point2D - The 2D point to find the closest boundary point for
 * @param {Array<TPoint2D>} polygon2D - The 2D polygon points
 * @returns {TPoint2D|null} The closest point on the polygon boundary, or null if polygon is invalid
 */
export const closestPointOnPolygon = (
	point2D: TPoint2D,
	polygon2D: Array<TPoint2D>,
): TPoint2D | null => {
	if (polygon2D.length < 2) return null;

	const point = new Point(point2D[0], point2D[1]);
	const polygon = new Polygon(polygon2D.map(([x, y]) => new Point(x, y)));

	const [, shortestSegment] = point.distanceTo(polygon);

	return [shortestSegment.pe.x, shortestSegment.pe.y];
};
